import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Confirm,
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  CmsPageMarket,
  Testimonial,
  TestimonialStatus,
} from '../dataProvider';

/**
 * The testimonial authoring form (D-cms-testimonials-seo). A FLAT structured CRUD form
 * (quote, author, rating, market), NO rich text. The lifecycle is the active / inactive
 * TOGGLE only (deactivate-not-delete, the charities pattern), NOT a publish lifecycle: a
 * testimonial has no body to compose or a goes-live act. SAVE writes the content fields;
 * the toggle flips status through the chokepoint (cms:author), audited. The `author` is a
 * byline NAME string, NOT a user FK. Authoring gates cms:author, operational
 * market-scoped (market is Market | 'all').
 */

const RATINGS = [1, 2, 3, 4, 5];

const TestimonialFormInner = ({ record }: { record?: Testimonial }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );
  // A testimonial may target a single in-scope market OR both (the RuleMarket idiom).
  const marketChoices: CmsPageMarket[] = [...markets, 'all'];

  const [quote, setQuote] = useState(record?.quote ?? '');
  const [author, setAuthor] = useState(record?.author ?? '');
  const [rating, setRating] = useState(record?.rating ?? 5);
  const [market, setMarket] = useState<CmsPageMarket>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );
  const [pendingToggle, setPendingToggle] = useState(false);
  const [busy, setBusy] = useState(false);

  // The toggle is an authoring act (cms:author), there is no separate publish capability.
  const canToggle = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );

  const validate = (): string | null => {
    if (!quote.trim()) return 'A quote is required';
    if (!author.trim()) return 'An author is required';
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    const nowIso = new Date().toISOString();
    try {
      if (isEdit && record) {
        await dataProvider.update('testimonials', {
          id: record.id,
          data: { quote, author, rating, market, updatedAt: nowIso },
          previousData: record,
        });
        notify('Testimonial saved', { type: 'success' });
        redirect('list', 'testimonials');
      } else {
        const { data } = await dataProvider.create('testimonials', {
          data: {
            quote,
            author,
            rating,
            market,
            // A new testimonial starts active (the on / off toggle, not a publish act).
            status: 'active' as TestimonialStatus,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        });
        notify('Testimonial created', { type: 'success' });
        redirect('edit', 'testimonials', data.id);
      }
    } catch {
      notify('Could not save the testimonial', { type: 'error' });
    }
  };

  // Flip active <-> inactive through the chokepoint (deactivate-not-delete), audited.
  const confirmToggle = async () => {
    if (!record) return;
    setBusy(true);
    const next: TestimonialStatus =
      record.status === 'active' ? 'inactive' : 'active';
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'cms:author',
        resource: 'testimonials',
        id: record.id,
        data: { status: next, updatedAt: new Date().toISOString() },
        previousData: record,
      });
      notify(
        next === 'active' ? 'Testimonial activated' : 'Testimonial deactivated',
        {
          type: 'success',
        },
      );
      setPendingToggle(false);
      refresh();
    } catch {
      notify('Could not change the status', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const isActive = record?.status === 'active';

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit testimonial' : 'New testimonial'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="Quote"
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'testimonial-quote' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              helperText="The byline (may credit a member or brand)"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'testimonial-author' }}
            />
            <TextField
              select
              label="Rating"
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              margin="dense"
              size="small"
              sx={{ minWidth: 120 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'testimonial-rating' }}
            >
              {RATINGS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as CmsPageMarket)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'testimonial-market' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m === 'all' ? 'Both markets' : m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {isEdit && record && (
        <Card sx={{ mb: 2 }} data-testid="testimonial-toggle">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="subtitle2">Status</Typography>
              <Chip
                size="small"
                color={isActive ? 'success' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                label={isActive ? 'Active' : 'Inactive'}
                data-testid="testimonial-status-chip"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              An inactive testimonial is hidden from the marketing site. This is
              the on / off toggle, the testimonial is never deleted.
            </Typography>
            {canToggle && (
              <Button
                variant="contained"
                color={isActive ? 'warning' : 'success'}
                disabled={busy}
                onClick={() => setPendingToggle(true)}
                data-testid="testimonial-toggle-button"
              >
                {isActive ? 'Deactivate' : 'Activate'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={save}
          data-testid="testimonial-save"
        >
          {isEdit ? 'Save' : 'Create testimonial'}
        </Button>
        <Button onClick={() => redirect('list', 'testimonials')}>Cancel</Button>
      </Stack>

      <Confirm
        isOpen={pendingToggle}
        loading={busy}
        title={
          isActive ? 'Deactivate this testimonial' : 'Activate this testimonial'
        }
        content={
          isActive
            ? 'This hides the testimonial from the marketing site. You can reactivate it later.'
            : 'This shows the testimonial on the marketing site.'
        }
        confirm="Confirm"
        cancel="Cancel"
        onConfirm={confirmToggle}
        onClose={() => setPendingToggle(false)}
      />
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const TestimonialCreateView = () => <TestimonialFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const TestimonialEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<Testimonial>('testimonials', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <TestimonialFormInner record={record} />;
};

export const TestimonialCreate = () => (
  <RequireAccess capability="cms:author">
    <TestimonialCreateView />
  </RequireAccess>
);

export const TestimonialEdit = () => (
  <RequireAccess capability="cms:author">
    <TestimonialEditView />
  </RequireAccess>
);
